'use strict'

const { flattenCardFields } = require('./card-layout')
const { pivotFieldNames } = require('./pivot-table')
const { relatedSpecFromKey } = require('./related-view-columns')
const { customViewFieldNames } = require('./custom-view')

// Optional text search on a multi-record view. The live app sends the
// typed text. The server chooses the fields from the saved view, so a
// caller cannot switch the scope or name extra columns.

const MULTI_RECORD_VIEW_TYPES = ['table', 'cards', 'maps', 'calendar', 'pivot', 'custom']

const SEARCH_SCOPES = ['view', 'spreadsheet']

const SKIP_TYPES = new Set(['password', 'image', 'file', 'relationship', 'signature'])

const SEARCHABLE_TYPES = new Set(['text', 'number', 'dateTime', 'richText', 'formula', 'multiselect', 'address', 'duration', 'sequence'])

const SYSTEM_FIELDS = [
  { uniqueTitle: 'id', type: 'number' },
  { uniqueTitle: 'createdAt', type: 'dateTime' },
  { uniqueTitle: 'updatedAt', type: 'dateTime' }
]

const FIELD_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/

const MAX_SEARCH_LENGTH = 200

function viewSearchApplies (component) {

  if (!component || !MULTI_RECORD_VIEW_TYPES.includes(component.type)) return false

  return component.searchEnabled === true || component.searchEnabled === 'true'
}

function viewSearchScope (component) {

  return component && component.searchScope === 'spreadsheet' ? 'spreadsheet' : 'view'
}

function normalizeSearchTerm (value) {

  if (value === undefined || value === null) return ''

  const term = String(value).trim()

  if (!term) return ''

  return term.slice(0, MAX_SEARCH_LENGTH)
}

function viewSearchParam (uniqueTitle) {

  const id = String(uniqueTitle || '').replace(/[^A-Za-z0-9_]/g, '')

  return id ? `s_${id}` : 's'
}

function columnByTitle (spreadsheet) {

  const columns = new Map()

  for (const field of SYSTEM_FIELDS) columns.set(field.uniqueTitle, field)

  for (const column of (spreadsheet && spreadsheet.columns) || []) {

    if (column && column.uniqueTitle) columns.set(column.uniqueTitle, column)
  }

  return columns
}

function isSearchableColumn (column) {

  if (!column || !FIELD_NAME.test(column.uniqueTitle)) return false
  if (SKIP_TYPES.has(column.type)) return false
  if (column.type && !SEARCHABLE_TYPES.has(column.type)) return false

  return true
}

function directTarget (uniqueTitle) {

  return {
    source: 'direct',
    field: uniqueTitle,
    operator: 'search',
    dataType: 'text',
    valueType: 'literal'
  }
}

function sheetByTitle (allSpreadsheets, uniqueTitle) {

  return (allSpreadsheets || []).find(sheet => sheet && sheet.uniqueTitle === uniqueTitle) || null
}

function relatedFieldColumn (component, allSpreadsheets) {

  const sheet = sheetByTitle(allSpreadsheets, component.relatedSpreadSheet)
    || sheetByTitle(allSpreadsheets, component.sourceSpreadsheet)

  if (!sheet) return null

  if (SYSTEM_FIELDS.some(field => field.uniqueTitle === component.relatedField)) {

    return SYSTEM_FIELDS.find(field => field.uniqueTitle === component.relatedField)
  }

  return (sheet.columns || []).find(column => column && column.uniqueTitle === component.relatedField) || null
}

function relatedTarget (component, allSpreadsheets) {

  if (!component || (component.relatedSource !== 'forward' && component.relatedSource !== 'reverse')) return null
  if (!FIELD_NAME.test(component.relatedField || '')) return null

  const column = relatedFieldColumn(component, allSpreadsheets)

  if (column && !isSearchableColumn(column)) return null
  if (!column && allSpreadsheets && allSpreadsheets.length) return null

  if (component.relatedSource === 'forward') {

    if (!FIELD_NAME.test(component.relationshipColumn || '')) return null
    if (!FIELD_NAME.test(component.relatedSpreadSheet || '')) return null

    return {
      source: 'forward',
      field: component.relatedField,
      relationshipColumn: component.relationshipColumn,
      relatedSpreadsheet: component.relatedSpreadSheet,
      operator: 'search',
      dataType: 'text',
      valueType: 'literal',
      relQuantifier: 'any'
    }
  }

  if (!FIELD_NAME.test(component.sourceSpreadsheet || '')) return null
  if (!FIELD_NAME.test(component.sourceRelationshipColumn || '')) return null

  return {
    source: 'reverse',
    field: component.relatedField,
    sourceSpreadsheet: component.sourceSpreadsheet,
    sourceRelationshipColumn: component.sourceRelationshipColumn,
    operator: 'search',
    dataType: 'text',
    valueType: 'literal',
    relQuantifier: 'any'
  }
}

function targetKey (target) {

  if (target.source === 'forward') {

    return `forward:${target.relationshipColumn}:${target.relatedSpreadsheet}:${target.field}`
  }

  if (target.source === 'reverse') {

    return `reverse:${target.sourceSpreadsheet}:${target.sourceRelationshipColumn}:${target.field}`
  }

  return `direct:${target.field}`
}

function pushTarget (targets, seen, target) {

  if (!target) return

  const key = targetKey(target)

  if (seen.has(key)) return

  seen.add(key)
  targets.push(target)
}

function viewFieldTargets (component, spreadsheet, allSpreadsheets) {

  const columns = columnByTitle(spreadsheet)
  const targets = []
  const seen = new Set()

  for (const field of flattenCardFields(component && component.components)) {

    if (!field) continue

    if (field.relatedSource === 'forward' || field.relatedSource === 'reverse') {

      pushTarget(targets, seen, relatedTarget(field, allSpreadsheets))
      continue
    }

    const column = columns.get(field.uniqueTitle)

    if (!isSearchableColumn(column)) continue

    pushTarget(targets, seen, directTarget(column.uniqueTitle))
  }

  if (component && component.type === 'calendar') {

    for (const uniqueTitle of [component.dateColumn, component.titleColumn, component.descriptionColumn]) {

      const column = columns.get(uniqueTitle)

      if (!isSearchableColumn(column)) continue

      pushTarget(targets, seen, directTarget(column.uniqueTitle))
    }
  }

  if (component && component.type === 'pivot') {

    for (const uniqueTitle of pivotFieldNames(component)) {

      const spec = relatedSpecFromKey(uniqueTitle)

      if (spec) {

        if (spec.relatedSource === 'forward') {

          const relationship = ((spreadsheet && spreadsheet.columns) || []).find(column => column && column.uniqueTitle === spec.relationshipColumn)

          spec.relatedSpreadSheet = relationship && relationship.relatedSpreadSheet
        }

        pushTarget(targets, seen, relatedTarget(spec, allSpreadsheets))
        continue
      }

      const column = columns.get(uniqueTitle)

      if (!isSearchableColumn(column)) continue

      pushTarget(targets, seen, directTarget(column.uniqueTitle))
    }
  }

  if (component && component.type === 'custom') {

    for (const uniqueTitle of customViewFieldNames(component.viewCode)) {

      const spec = relatedSpecFromKey(uniqueTitle)

      if (spec) {

        if (spec.relatedSource === 'forward') {

          const relationship = ((spreadsheet && spreadsheet.columns) || []).find((column) => column && column.uniqueTitle === spec.relationshipColumn)

          spec.relatedSpreadSheet = relationship && relationship.relatedSpreadSheet
        }

        pushTarget(targets, seen, relatedTarget(spec, allSpreadsheets))
        continue
      }

      const column = columns.get(uniqueTitle)

      if (!isSearchableColumn(column)) continue

      pushTarget(targets, seen, directTarget(column.uniqueTitle))
    }
  }

  return targets
}

function spreadsheetFieldTargets (spreadsheet) {

  const targets = []
  const seen = new Set()

  for (const field of SYSTEM_FIELDS) {

    pushTarget(targets, seen, directTarget(field.uniqueTitle))
  }

  for (const column of (spreadsheet && spreadsheet.columns) || []) {

    if (!isSearchableColumn(column)) continue

    pushTarget(targets, seen, directTarget(column.uniqueTitle))
  }

  return targets
}

function searchTargets (component, spreadsheet, allSpreadsheets) {

  if (viewSearchScope(component) === 'spreadsheet') return spreadsheetFieldTargets(spreadsheet)

  return viewFieldTargets(component, spreadsheet, allSpreadsheets)
}

function searchFilters (component, spreadsheet, allSpreadsheets, term) {

  if (!viewSearchApplies(component)) return null

  const normalized = normalizeSearchTerm(term)

  if (!normalized) return null

  const targets = searchTargets(component, spreadsheet, allSpreadsheets)

  if (!targets.length) return []

  return targets.map(target => ({ ...target, value: normalized }))
}

module.exports = {
  MULTI_RECORD_VIEW_TYPES,
  SEARCH_SCOPES,
  MAX_SEARCH_LENGTH,
  viewSearchApplies,
  viewSearchScope,
  normalizeSearchTerm,
  viewSearchParam,
  searchTargets,
  searchFilters
}
